import React, { useState, useCallback, useEffect } from "react";
import {
  Typography,
  Icon,
  Tag,
  Button,
  Avatar,
  IconHighlight,
} from "@ann/web-components";
import { useFactDetail } from "../../hooks";
import { useMyMemberContext } from "../../../../context";
import {
  EditGeneralInformationModal,
  EditDescriptionModal,
  LinkSourceDocumentModal,
} from "../../../../components";
import {
  OverviewContainer,
  SectionCard,
  SectionHeader,
  SectionTitle,
  InfoGrid,
  InfoItem,
  InfoLabel,
  InfoValue,
  UserInfo,
  SourceCard,
  SourceContent,
  SourceTitle,
  SourceFileName,
  SourceDescription,
} from "./Overview.styled";
import dayjs from "dayjs";

const Overview = ({ factId }) => {
  const { factData, loading, refetch } = useFactDetail(factId);
  const { myRole } = useMyMemberContext();
  const [isEditGeneralModalOpen, setIsEditGeneralModalOpen] = useState(false);
  const [isEditDescriptionModalOpen, setIsEditDescriptionModalOpen] = useState(false);
  const [isLinkSourceModalOpen, setIsLinkSourceModalOpen] = useState(false);
  useEffect(() => {
    console.log("factData", factData);
  }, [factData]);

  // Determinar permisos según el rol
  const canEdit = myRole === 'ADMIN';

  const handleEditGeneralSave = useCallback((updatedData) => {
    console.log("General information updated:", updatedData);
    // TODO: Refresh fact data when API is ready
    refetch();
  }, [refetch]);

  const handleEditDescriptionSave = useCallback((description) => {
    console.log("Description updated:", description);
    // TODO: Refresh fact data when API is ready
    refetch();
  }, [refetch]);

  const handleLinkSource = useCallback((linkData) => {
    console.log("Source linked:", linkData);
    // TODO: Refresh fact data when API is ready
    refetch();
  }, [refetch]);

  if (loading) {
    return (
      <OverviewContainer>
        <div>Loading...</div>
      </OverviewContainer>
    );
  }

  if (!factData) {
    return (
      <OverviewContainer>
        <div>Fact not found</div>
      </OverviewContainer>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = dayjs(dateString);
    return date.format("MMM D, YYYY");
  };

  const getStatusColor = (status) => {
    const normalizedStatus =
      typeof status === "string" ? status.toLowerCase() : "";
    switch (normalizedStatus) {
      case "active":
        return "success";
      case "pending":
      case "pending approval":
        return "neutral";
      case "expired":
        return "error";
      default:
        return "neutral";
    }
  };

  const getAvatarUrl = (userName, initials) => {
    if (!userName || userName === "—") {
      return null;
    }
    const name = initials || userName;
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(
      name
    )}&background=2173FF&color=fff`;
  };

  const createdByAvatar = getAvatarUrl(
    factData.createdBy,
    factData.createdByInitials
  );
  const verifiedByAvatar = factData.verifiedBy
    ? getAvatarUrl(factData.verifiedBy, factData.verifiedByInitials)
    : null;

  return (
    <OverviewContainer>
      <SectionHeader>
        <Typography
          category="subtitle"
          size="small"
          weight="bold"
          color="subtitle"
        >
          General Information
        </Typography>
        <Button
          type="secondary"
          label="Edit information"
          size="small"
          pill={true}
          iconLeft="edit"
          onClick={() => setIsEditGeneralModalOpen(true)}
          // disabled={!canEdit} // TODO: Descomentar cuando myMember está funcionando
        />
      </SectionHeader>
      <SectionCard>
        <InfoGrid>
          <InfoItem>
            <InfoLabel>
              <Typography
                category="body"
                size="medium"
                weight="regular"
                color="subTitle"
              >
                Created By
              </Typography>
            </InfoLabel>
            <InfoValue>
              <UserInfo>
                {createdByAvatar && (
                  <Avatar
                    size="sm"
                    src={createdByAvatar}
                    alt={factData.createdBy}
                  />
                )}
                <Typography
                  category="body"
                  size="medium"
                  weight="regular"
                  color="body"
                  maxCharacters={20}
                >
                  {factData.createdBy || "—"}
                </Typography>
              </UserInfo>
            </InfoValue>
          </InfoItem>

          <InfoItem>
            <InfoLabel>
              <Typography
                category="body"
                size="medium"
                weight="regular"
                color="subTitle"
              >
                Verified by
              </Typography>
            </InfoLabel>
            <InfoValue>
              <UserInfo>
                {verifiedByAvatar && (
                  <Avatar
                    size="sm"
                    src={verifiedByAvatar}
                    alt={factData.verifiedBy}
                  />
                )}
                <Typography
                  category="body"
                  size="medium"
                  weight="regular"
                  color="body"
                  maxCharacters={20}
                >
                  {factData.verifiedBy
                    ? `${factData.verifiedBy} · ${formatDate(
                        factData.verifiedOn
                      )}`
                    : "Not Verified"}
                </Typography>
              </UserInfo>
            </InfoValue>
          </InfoItem>

          <InfoItem>
            <InfoLabel>
              <Typography
                category="body"
                size="medium"
                weight="regular"
                color="subTitle"
              >
                Created on
              </Typography>
            </InfoLabel>
            <InfoValue>
              <Icon name="calendarToday" size="small" />
              <Typography
                category="body"
                size="medium"
                weight="regular"
                color="body"
                maxCharacters={20}
              >
                {formatDate(factData.createdOn)}
              </Typography>
            </InfoValue>
          </InfoItem>

          <InfoItem>
            <InfoLabel>
              <Typography
                category="body"
                size="medium"
                weight="regular"
                color="subTitle"
              >
                Status
              </Typography>
            </InfoLabel>
            <InfoValue>
              <Tag
                color={getStatusColor(factData.status)}
                size="medium"
                text={
                  factData.status
                    ? factData.status.charAt(0).toUpperCase() +
                      factData.status.slice(1).toLowerCase()
                    : "—"
                }
              />
            </InfoValue>
          </InfoItem>

          <InfoItem>
            <InfoLabel>
              <Typography
                category="body"
                size="medium"
                weight="regular"
                color="subTitle"
              >
                Last Updated
              </Typography>
            </InfoLabel>
            <InfoValue>
              <Icon name="schedule" size="small" />
              <Typography
                category="body"
                size="medium"
                weight="regular"
                color="body"
                maxCharacters={20}
              >
                {formatDate(factData.lastUpdated)}
              </Typography>
            </InfoValue>
          </InfoItem>

          <InfoItem>
            <InfoLabel>
              <Typography
                category="body"
                size="medium"
                weight="regular"
                color="subTitle"
              >
                Expiration (TTL)
              </Typography>
            </InfoLabel>
            <InfoValue>
              <Typography
                category="body"
                size="medium"
                weight="regular"
                color="body"
                maxCharacters={20}
                >
                {factData.expiration || "—"}
              </Typography>
              <Icon
                name="edit"
                size="small"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  // TODO: Implement edit expiration
                  console.log("Edit expiration clicked");
                }}
              />
            </InfoValue>
          </InfoItem>
        </InfoGrid>
      </SectionCard>

      {/* Fact Description Section */}
      <SectionHeader>
        <SectionTitle>
          <Typography
            category="subtitle"
            size="small"
            weight="bold"
            color="subtitle"
          >
            Fact Description
          </Typography>
          <Icon name="error" size="small" />
        </SectionTitle>
        <Button
          type="secondary"
          label="Edit"
          size="small"
          pill={true}
          iconLeft="edit"
          onClick={() => setIsEditDescriptionModalOpen(true)}
          // disabled={!canEdit} // TODO: Descomentar cuando myMember está funcionando
        />
      </SectionHeader>

      <SectionCard>
        <Typography category="body" size="medium" weight="regular" color="body">
          {factData.factDescription || "—"}
        </Typography>
      </SectionCard>

      {/* Source Section */}
      <SectionHeader>
        <SectionTitle>
          <Typography
            category="subtitle"
            size="small"
            weight="bold"
            color="subtitle"
          >
            Source
          </Typography>
          <Icon name="error" size="small" />
        </SectionTitle>
        <Button
          type="secondary"
          label="Edit Source"
          size="small"
          pill={true}
          iconLeft="edit"
          onClick={() => setIsLinkSourceModalOpen(true)}
          // disabled={!canEdit} // TODO: Descomentar cuando myMember está funcionando
        />
      </SectionHeader>
      <SectionCard>
        {factData.sourceDocument ? (
          <SourceCard>
            <IconHighlight
              bgColor="LightBlue"
              icon="description"
              iconColor="Success"
              size="medium"
            />
            <SourceContent>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <SourceTitle>
                  <Typography category="subtitle" size="small" weight="bold" color="subtitle">
                    Document
                  </Typography>
                </SourceTitle>
                <Tag
                  color="informative"
                  size="small"
                  text={factData.sourceDocument.type || "Doc"}
                />
              </div>
              <SourceFileName>
                <Typography category="body" size="medium" weight="regular" color="body">
                  {factData.sourceDocument.name}
                </Typography>
              </SourceFileName>
              <SourceDescription>
                <Typography category="body" size="small" weight="regular" color="body">
                  {factData.sourceDocument.description}
                </Typography>
              </SourceDescription>
            </SourceContent>
          </SourceCard>
        ) : (
          <Typography category="body" size="medium" weight="regular">
            {factData.source || "—"}
          </Typography>
        )}
      </SectionCard>

      {/* Modals */}
      <EditGeneralInformationModal
        isOpen={isEditGeneralModalOpen}
        onClose={() => setIsEditGeneralModalOpen(false)}
        factData={factData}
        onSave={handleEditGeneralSave}
      />

      <EditDescriptionModal
        isOpen={isEditDescriptionModalOpen}
        onClose={() => setIsEditDescriptionModalOpen(false)}
        factData={factData}
        onSave={handleEditDescriptionSave}
      />

      <LinkSourceDocumentModal
        isOpen={isLinkSourceModalOpen}
        onClose={() => setIsLinkSourceModalOpen(false)}
        factData={factData}
        onLink={handleLinkSource}
      />
    </OverviewContainer>
  );
};

export default Overview;
